import { Modal, Form } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { useText } from "../context/TextContext";
import { AdvancedButton } from "./AdvancedButton";
import { toast } from "react-toastify";
import { formatDuration, formatDurationFromMs, parseDurationMs, formatDate } from "../utils/date";
import DealPicker from "./DealPicker";
import { useFirestore } from "reactfire";
import { useUser } from "../context/UserContext";
import { useOrganization } from "../context/OrganizationContext";
import { addDoc, collection, updateDoc, doc, where } from "firebase/firestore";
import { COLLECTIONS } from "../utils/shared/constants";
import { TimeRecord } from "../models/shared/TimeRecord";
import { Article } from "../models/shared/Article";
import { sortArticles } from "../utils/shared/article";
import OrgMemberSelect from "./OrgMemberSelect";
import { useFirebaseQuery } from "../hooks/firebaseHooks";

interface Props {
  onClose: () => void;
  initialData?: TimeRecord;
  modal?: boolean;
  dealId?: string;
}

interface Input {
  startAt: Date;
  endAt: Date;
  dealId: string;
  notes: string;
  userEmail?: string;
}

export default function TimesheetEntryForm({ onClose, initialData, modal, dealId }: Props) {
  const { register, handleSubmit, formState: { errors }, watch, control, setValue } = useForm<Input>();
  const text = useText();
  const [notes, setNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const firestore = useFirestore();
  const { user } = useUser();
  const { orgId, orgRole } = useOrganization();

  const [localDuration, setLocalDuration] = useState("00:00");
  const [localEndAtString, setLocalEndAtString] = useState("");
  const [activityDurations, setActivityDurations] = useState<Record<string, string>>({});

  const watchedStartAt = watch("startAt");
  const disableUserPicker = orgRole === 'worker';

  const [unsortedActivities] = useFirebaseQuery<Article>(
    `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.ARTICLES}`,
    where('status', '==', 'active'),
    where('isActivity', '==', true),
  );
  const activities = useMemo(() => sortArticles(unsortedActivities), [unsortedActivities]);
  const requiresActivitySplit = activities.length > 1;

  const totalMinutes = Math.round(parseDurationMs(localDuration) / 60000);
  const assignedMinutes = activities.reduce(
    (sum, activity) => sum + Math.round(parseDurationMs(activityDurations[activity.id || ''] || '00:00') / 60000),
    0,
  );
  const remainingMinutes = totalMinutes - assignedMinutes;

  const handleActivityDurationChange = (activityId: string, value: string) => {
    setActivityDurations(prev => ({ ...prev, [activityId]: value }));
  };

  const handleFillRemaining = (activityId: string) => {
    const currentMinutes = Math.round(parseDurationMs(activityDurations[activityId] || '00:00') / 60000);
    const newMinutes = Math.max(0, currentMinutes + remainingMinutes);
    setActivityDurations(prev => ({ ...prev, [activityId]: formatDurationFromMs(newMinutes * 60000) }));
  };


  useEffect(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    const fourHoursAgo = new Date(now.getTime() - 4 * 60 * 60 * 1000);

    if (initialData) {
      setValue('startAt', formatDate(initialData.startAt || fourHoursAgo, { isoDate: true, noSeconds: true }) as any);
      setValue('endAt', initialData.endAt || now);
      setLocalEndAtString(initialData.endAt ? formatDate(initialData.endAt, { isoDate: true, noSeconds: true }) : "");
      setValue('notes', initialData.notes || "");
      setValue('userEmail', initialData.email);
      setValue('dealId', initialData.dealId);
      setNotes(initialData.notes || "");
      if (initialData.startAt && initialData.endAt) {
        setLocalDuration(formatDuration(new Date(initialData.startAt), new Date(initialData.endAt)));
      }
      const splitMap: Record<string, string> = {};
      for (const split of initialData.activitySplits || []) {
        splitMap[split.activityId] = formatDurationFromMs(split.durationMs);
      }
      setActivityDurations(splitMap);
    } else {
      setValue('startAt', formatDate(fourHoursAgo, { isoDate: true, noSeconds: true }) as any);
      setValue('endAt', now);
      setLocalEndAtString(formatDate(now, { isoDate: true, noSeconds: true }));
      if (user?.email) {
        setValue('userEmail', user.email);
      }
      if (dealId) {
        setValue('dealId', dealId);
      }
    }
  }, [initialData, user?.email, setValue, dealId]);

  useEffect(() => {
    if (!watchedStartAt || isNaN(watchedStartAt.getTime())) return;

    const endAtDate = new Date(localEndAtString);
    if (isNaN(endAtDate.getTime())) return;

    setLocalDuration(formatDuration(watchedStartAt, endAtDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedStartAt]);

  const handleSave = async (data: Input) => {
    if (requiresActivitySplit && remainingMinutes !== 0) {
      toast.error(text.activitySplitMismatch);
      return;
    }

    setProcessing(true);

    const timeRecordUpdate: Partial<TimeRecord> = {
      dealId: data.dealId,
      email: data.userEmail || "",
      startAt: data.startAt,
      endAt: data.endAt,
      notes: notes,
      ...(requiresActivitySplit ? {
        activitySplits: activities
          .map(activity => ({
            activityId: activity.id || '',
            durationMs: parseDurationMs(activityDurations[activity.id || ''] || '00:00'),
          }))
          .filter(split => split.durationMs > 0)
      } : {}),
    };

    try {
      if (initialData?.id) {
        const recordRef = doc(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}/${initialData.id}`);
        await updateDoc(recordRef, timeRecordUpdate);
      } else {
        await addDoc(
          collection(firestore, `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.TIME_RECORDS}`), timeRecordUpdate
        );
      }
      toast.success(text.defaultSuccess);
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(text.defaultError);
    }
    setProcessing(false);
  };

  const formContent = (
    <>
      <OrgMemberSelect
        value={watch('userEmail')}
        onChange={val => setValue('userEmail', val)}
        disabled={disableUserPicker}
        label={text.user}
        error={!!errors.userEmail}
      />

      {!dealId && <Form.Group>
        <Form.Label className="fw-semibold">{text.deal}</Form.Label>
        <Controller
          name="dealId"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, onBlur, value } }) => (
            <DealPicker
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={!!errors.dealId}
              disabled={!!initialData}
            />
          )}
        />
      </Form.Group>}

      <Form.Group>
        <div className="fw-semibold mb-2">{text.start}</div>
        <Form.Control
          type="datetime-local"
          isInvalid={!!errors.startAt}
          {...register('startAt', { required: true, setValueAs: (v) => v ? new Date(v) : null })}
        />
      </Form.Group>

      <Controller
        name="endAt"
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            if (watchedStartAt && value) {
              return value.getTime() >= watchedStartAt.getTime() || text.endDateAfterStartDate;
            }
            return true;
          }
        }}
        render={({ field: { onChange }, fieldState: { error } }) => {
          const handleEndAtChange = (newEndAtString: string) => {
            setLocalEndAtString(newEndAtString);
            const endAtDate = new Date(newEndAtString);


            if (!isNaN(endAtDate.getTime())) {
              onChange(endAtDate);
              setLocalDuration(formatDuration(watchedStartAt, endAtDate));
            }
          };

          const handleDurationChange = (newDuration: string) => {
            setLocalDuration(newDuration);
            const durationMs = parseDurationMs(newDuration);
            if (watchedStartAt && durationMs) {
              const newEndAtDate = new Date(watchedStartAt.getTime() + durationMs);
              onChange(newEndAtDate);
              setLocalEndAtString(formatDate(newEndAtDate, { isoDate: true }));
            }
          }
          return (
            <>
              <Form.Group>
                <div className="fw-semibold mb-2">{text.end}</div>
                <Form.Control
                  type="datetime-local"
                  isInvalid={!!error}
                  value={localEndAtString}
                  onChange={(e) => handleEndAtChange(e.target.value)}
                />
                {error && <Form.Control.Feedback type="invalid">{error.message}</Form.Control.Feedback>}
              </Form.Group>

              <Form.Group>
                <Form.Label className="fw-semibold">{text.totalTimeHoursMinutes}</Form.Label>
                <Form.Control
                  isInvalid={!!error}
                  value={localDuration}
                  onChange={(e) => handleDurationChange(e.target.value)}
                />
              </Form.Group>
            </>
          )
        }}
      />

      {requiresActivitySplit && (
        <Form.Group>
          <Form.Label className="fw-semibold">{text.splitHoursByActivity}</Form.Label>
          <div className="d-flex flex-column gap-2">
            {activities.map(activity => (
              <div key={activity.id} className="d-flex align-items-center gap-2">
                <div className="flex-grow-1">{activity.name}</div>
                <Form.Control
                  size="sm"
                  style={{ maxWidth: 90 }}
                  value={activityDurations[activity.id || ''] || '00:00'}
                  onChange={(e) => handleActivityDurationChange(activity.id || '', e.target.value)}
                />
                <AdvancedButton
                  variant="outline-secondary"
                  size="sm"
                  icon="bi-arrow-bar-right"
                  disabled={remainingMinutes <= 0}
                  onClick={() => handleFillRemaining(activity.id || '')}
                >
                  {text.fillRemaining}
                </AdvancedButton>
              </div>
            ))}
          </div>
          <div className={`small mt-2 ${remainingMinutes !== 0 ? 'text-danger' : 'text-muted'}`}>
            {text.assignedHours}: {formatDurationFromMs(assignedMinutes * 60000)} / {formatDurationFromMs(totalMinutes * 60000)}
            {' — '}{text.remainingHours}: {remainingMinutes < 0 ? '-' : ''}{formatDurationFromMs(Math.abs(remainingMinutes) * 60000)}
          </div>
        </Form.Group>
      )}

      <Form.Group>
        <Form.Label className="fw-semibold">{text.notes}</Form.Label>
        <Form.Control as="textarea" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Form.Group>
    </>
  );

  const footerContent = (
    <>
      <AdvancedButton variant="outline-secondary" onClick={onClose} icon='bi-x'>
        {text.cancel}
      </AdvancedButton>
      <AdvancedButton type="submit" variant="primary" icon='bi-floppy' processing={processing}>
        {text.save}
      </AdvancedButton>
    </>
  );

  return (
    <form onSubmit={handleSubmit(handleSave)}>
      {modal ? (
        <>
          <Modal.Body className="p-4 d-flex flex-column gap-3">
            {formContent}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-end gap-2">
            {footerContent}
          </Modal.Footer>
        </>
      ) : (
        <div className="d-flex flex-column gap-3">
          <div className="d-flex flex-column gap-3">
            {formContent}
          </div>
          <div className="d-flex justify-content-end gap-2">
            {footerContent}
          </div>
        </div>
      )}
    </form>
  );
}